'use client'

import { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuth } from '@/context/AuthContext'
import { resendConfirmation } from '@/lib/supabase'

/* ── Icons ──────────────────────────────────────────────────────────── */
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  )
}

/* ── Hero stats ─────────────────────────────────────────────────────── */
const STATS = [
  { value: '60', label: 'European airports' },
  { value: '6', label: 'continents' },
  { value: '24/7', label: 'price tracking' },
]

/* ── Form (uses useSearchParams — must be wrapped in Suspense) ───────── */
function SignInForm() {
  const { currentUser, signIn, signUp, signInWithGoogle } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()

  const initMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin'
  const [mode, setMode] = useState(initMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resendEmail, setResendEmail] = useState('')
  const [resendSent, setResendSent]   = useState(false)

  const redirectTo = searchParams.get('from') || '/app'

  // Already signed in → skip to destination
  useEffect(() => {
    if (currentUser) router.replace(redirectTo)
  }, [currentUser, redirectTo, router])

  function switchMode(m) { setMode(m); setError('') }

  async function handleGoogleSignIn() {
    setError('')
    setLoading(true)
    try {
      await signInWithGoogle()
      window.plausible?.('AuthSignin', { props: { method: 'google' } })
      router.push(redirectTo)
    } catch (err) {
      setError(err.message ?? 'Google sign-in failed. Please try again.')
      setLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signup') {
        await signUp(email, password)
        window.plausible?.('AuthSignup')
      } else {
        await signIn(email, password)
        window.plausible?.('AuthSignin', { props: { method: 'email' } })
      }
      router.push(redirectTo)
    } catch (err) {
      const msg = err.message ?? 'Authentication failed. Please try again.'
      setError(msg)
      if (msg.toLowerCase().includes('not confirmed')) setResendEmail(email)
      setLoading(false)
    }
  }

  async function handleResend() {
    if (!resendEmail) return
    setLoading(true)
    try {
      await resendConfirmation(resendEmail)
      setResendSent(true)
      setError('')
    } catch {
      setError('Kon bevestigingsmail niet versturen. Probeer opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col justify-center flex-1 px-8 py-12 lg:px-14">
      <div className="max-w-sm mx-auto w-full">

        {/* Logo */}
        <a href="/" className="inline-flex items-center gap-2 text-lg font-bold text-blue-600 mb-10 hover:opacity-90 transition-opacity">
          <img src="/logo-512.png" alt="FairFares" width="32" height="32" className="rounded-lg" />
          FairFares
        </a>

        {/* Heading — transitions smoothly between signin / signup */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1 transition-all">
          {mode === 'signin' ? 'Welcome back' : 'Create your account'}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
          {mode === 'signin'
            ? 'Sign in to access personalised deal alerts.'
            : 'Join thousands of travellers tracking flight deals.'}
        </p>

        {/* Google — primary CTA */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 transition-colors"
        >
          {loading ? <Spinner /> : <GoogleIcon />}
          Continue with Google
        </button>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-100 dark:border-gray-800" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white dark:bg-gray-950 px-3 text-xs text-gray-400">or continue with email</span>
          </div>
        </div>

        {/* Email / password form */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label htmlFor="signin-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Email
            </label>
            <input
              id="signin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            />
          </div>

          <div>
            <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Password
            </label>
            <input
              id="signin-password"
              type="password"
              required
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-xl border border-red-100 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-400"
            >
              <span aria-hidden="true" className="mt-0.5 shrink-0">⚠️</span>
              <span>{error}</span>
              {resendEmail && !resendSent && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="mt-1 block underline text-red-700 hover:text-red-900 font-medium text-xs"
                >
                  Bevestigingsmail opnieuw versturen →
                </button>
              )}
            </div>
          )}
          {resendSent && (
            <p role="status" className="text-sm text-green-700 bg-green-50 rounded-xl px-4 py-3">
              Bevestigingsmail opnieuw verstuurd — check je inbox (en spam).
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm shadow-blue-500/25 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 transition-colors"
          >
            {loading && <Spinner />}
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        {/* Mode toggle */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {mode === 'signin' ? (
            <>
              Don't have an account?{' '}
              <button
                onClick={() => switchMode('signup')}
                className="font-medium text-blue-600 hover:underline transition-colors"
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                onClick={() => switchMode('signin')}
                className="font-medium text-blue-600 hover:underline transition-colors"
              >
                Sign in
              </button>
            </>
          )}
        </p>

        {/* Trust badges */}
        <div className="mt-10 flex items-center justify-center gap-4 text-xs text-gray-400 dark:text-gray-600">
          <span>🔒 Secured by Firebase</span>
          <span aria-hidden="true">·</span>
          <span>🚫 No spam, ever</span>
        </div>
      </div>
    </div>
  )
}

/* ── Page ────────────────────────────────────────────────────────────── */
export default function SignInPage() {
  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .anim-1 { animation: fadeSlideUp 0.5s ease 0.05s both; }
        .anim-2 { animation: fadeSlideUp 0.5s ease 0.18s both; }
        .anim-3 { animation: fadeSlideUp 0.5s ease 0.30s both; }
      `}</style>

      <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-gray-950 max-w-5xl mx-auto">

        {/* ── Form side — left on desktop, bottom on mobile ── */}
        <div className="order-2 md:order-1 flex flex-col flex-1 md:w-1/2">
          <Suspense fallback={
            <div className="flex flex-1 items-center justify-center min-h-[60vh]">
              <div className="h-8 w-8 rounded-full border-[3px] border-blue-600 border-t-transparent animate-spin" />
            </div>
          }>
            <SignInForm />
          </Suspense>
        </div>

        {/* ── Hero side — right on desktop, compact banner on mobile ── */}
        <div className="hidden md:flex order-1 md:order-2 bg-blue-600 relative overflow-hidden md:w-1/2 items-center justify-center py-12 px-8 md:py-0">
          <div className="relative z-10 max-w-xs md:max-w-sm w-full text-white">

            <div className="anim-1">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-blue-300 mb-4">
                FairFares
              </span>
              <h2 className="text-3xl md:text-[2.6rem] font-extrabold leading-snug">
                Find flights at prices others miss
              </h2>
            </div>

            <div className="anim-2 mt-8 md:mt-10 grid grid-cols-3 gap-3">
              {STATS.map(({ value, label }) => (
                <div key={label} className="rounded-xl bg-white/10 backdrop-blur-sm px-3 py-4 text-center">
                  <div className="text-xl md:text-2xl font-bold">{value}</div>
                  <div className="mt-1 text-xs leading-tight text-blue-200">{label}</div>
                </div>
              ))}
            </div>

            <p className="anim-3 mt-8 text-sm text-blue-200 leading-relaxed hidden md:block">
              Trusted by travellers across Europe. No commissions — we never sell flights, just find them.
            </p>

          </div>
        </div>

      </div>
    </>
  )
}
